import type { CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';

import { MODEL_FAMILIES } from '../data/models';
import { formatUsd, priceOf } from '../data/prices';
import { FormatChips } from './FormatChips';

const money = (value: number | null | undefined) =>
  value === null || value === undefined ? '—' : formatUsd(value);

interface Props {
  caption: string;
  /** "featured": a few models per family (landing); "all": every model. */
  rows: 'featured' | 'all';
  cacheWrite?: boolean;
}

/** USD per 1M tokens, grouped by model family; each family keeps its route-map colour. */
export function PriceTable({ caption, rows, cacheWrite = false }: Props) {
  const { t } = useTranslation();
  const columns = cacheWrite ? 5 : 4;

  return (
    <table className="price-table">
      <caption className="visually-hidden">{caption}</caption>
      <thead>
        <tr>
          <th scope="col">{t('prices.model')}</th>
          <th scope="col">{t('prices.input')}</th>
          <th scope="col">{t('prices.output')}</th>
          <th scope="col">{t('prices.cacheRead')}</th>
          {cacheWrite && <th scope="col">{t('prices.cacheWrite')}</th>}
        </tr>
      </thead>
      {MODEL_FAMILIES.map((family, index) => (
        <tbody key={family.id} style={{ '--line': family.color, '--n': index } as CSSProperties}>
          <tr className="price-family">
            <th scope="rowgroup" colSpan={columns}>
              <span className="price-family-name">
                <span className="line-swatch" />
                {family.name}
              </span>
              <FormatChips formats={family.formats} />
            </th>
          </tr>
          {(rows === 'featured' ? family.featured : family.models).map((model) => {
            const price = priceOf(model);
            return (
              <tr key={model}>
                <th scope="row">
                  <code>{model}</code>
                </th>
                <td data-label={t('prices.input')}>{money(price?.input)}</td>
                <td data-label={t('prices.output')}>{money(price?.output)}</td>
                <td data-label={t('prices.cacheRead')}>{money(price?.cacheRead)}</td>
                {cacheWrite && (
                  <td data-label={t('prices.cacheWrite')}>{money(price?.cacheWrite)}</td>
                )}
              </tr>
            );
          })}
        </tbody>
      ))}
    </table>
  );
}
